// What a publish leaves behind, as one value driven by a pure reducer: the
// verdict that went in, whether a publish is running, why the last one was
// refused, and the hand-off's writes one by one (see logic/handoff for what
// they are). PrStore keeps it; the Review view and PrView read it.
import type { WriteError } from '../api/errors';
import { loginKey, type AttentionChange } from './attention';
import type { Verdict } from './inbox';
import { compareTimes } from './time';

/** One write of a hand-off: an attention change, or a review asked of someone. */
export type HandOffStep = { kind: 'attention'; change: AttentionChange } | { kind: 'review'; login: string };

/** What a step changes: one login's attention, or one login's review request. */
export const stepKey = (s: HandOffStep): string => `${s.kind}:${loginKey(s.kind === 'attention' ? s.change.login : s.login)}`;

/** `waiting`: an attention change held back because a review request of the same publish failed (Retry sends the request first). */
export type StepStatus = { at: 'sending' } | { at: 'done' } | { at: 'failed'; error: string } | { at: 'waiting' };

export interface StepState {
  step: HandOffStep;
  status: StepStatus;
}

export type AftermathRun =
  | { phase: 'idle' }
  | { phase: 'publishing' }
  /** The publish failed: nothing of it went in. */
  | { phase: 'refused'; error: WriteError }
  /**
   * Publish number `serial` went in. `basis` is the least basis for its
   * overrides: GitHub's time for the last thing it wrote, moved on by each
   * review it requested (null when none is known). With no steps left,
   * there is nothing to show.
   */
  | { phase: 'landed'; serial: number; basis: string | null; steps: StepState[] };

export interface Aftermath {
  /** The verdict of the last publish that went in; null before one, or when it was conversation comments only. */
  readonly sent: Verdict | null;
  readonly run: AftermathRun;
  /** Counts the steps that failed, so each failure is told once (a toast while the Review view is not shown). */
  readonly failures: number;
}

export type AftermathEvent =
  | { kind: 'publish' }
  | { kind: 'refused'; error: WriteError | null }
  | { kind: 'landed'; serial: number; verdict: Verdict | null; writtenAt: string | null; steps: HandOffStep[] }
  /** A step of publish `serial` was answered; `at` is GitHub's time for what it did, when it said (a review request's). */
  | { kind: 'step'; serial: number; key: string; result: { ok: true; at?: string } | { ok: false; error: string } }
  /** Hold publish `serial`'s steps `keys` (sending) until its failed review requests are sent again. */
  | { kind: 'hold'; serial: number; keys: string[] }
  /** Send the failed and held steps again. */
  | { kind: 'retry' }
  /** What a refused publish meant to send went in another way (the conversation drafts' own Retry): the refusal is over. */
  | { kind: 'cleared' }
  /** A later change for these steps' logins: the steps are retired, whatever their state (latest intent wins). */
  | { kind: 'superseded'; keys: string[] }
  /** The Review view was left: an error and finished steps are forgotten; failed and sending ones stay. */
  | { kind: 'left' };

export const IDLE_AFTERMATH: Aftermath = { sent: null, run: { phase: 'idle' }, failures: 0 };

const withSteps = (a: Aftermath, steps: StepState[]): Aftermath => (a.run.phase === 'landed' ? { ...a, run: { ...a.run, steps } } : a);

/** The later of two times; a missing one loses. */
const later = (a: string | null, b: string | undefined): string | null => (b && (!a || compareTimes(b, a) > 0) ? b : a);

export function aftermath(a: Aftermath, e: AftermathEvent): Aftermath {
  switch (e.kind) {
    case 'publish':
      // Not while the hand-off before it is still being sent.
      if (a.run.phase === 'landed' && a.run.steps.some((s) => s.status.at === 'sending')) return a;
      return { ...a, run: { phase: 'publishing' } };
    case 'refused':
      // A Retry refused while the hand-off is being sent leaves the hand-off shown.
      if (a.run.phase === 'landed' && a.run.steps.some((s) => s.status.at === 'sending')) return a;
      return { ...a, run: e.error ? { phase: 'refused', error: e.error } : { phase: 'idle' } };
    case 'cleared':
      return a.run.phase === 'refused' ? { ...a, run: { phase: 'idle' } } : a;
    case 'hold':
      if (a.run.phase !== 'landed' || a.run.serial !== e.serial) return a;
      return withSteps(
        a,
        a.run.steps.map((s) => (s.status.at === 'sending' && e.keys.includes(stepKey(s.step)) ? { ...s, status: { at: 'waiting' } } : s)),
      );
    case 'landed':
      return {
        ...a,
        sent: e.verdict,
        run: { phase: 'landed', serial: e.serial, basis: e.writtenAt, steps: e.steps.map((step) => ({ step, status: { at: 'sending' } })) },
      };
    case 'step': {
      if (a.run.phase !== 'landed' || a.run.serial !== e.serial) return a;
      const at = a.run.steps.findIndex((s) => stepKey(s.step) === e.key && s.status.at === 'sending');
      if (at < 0) return a;
      const status: StepStatus = e.result.ok ? { at: 'done' } : { at: 'failed', error: e.result.error };
      const steps = a.run.steps.map((s, i) => (i === at ? { ...s, status } : s));
      const basis = e.result.ok ? later(a.run.basis, e.result.at) : a.run.basis;
      return { ...a, run: { ...a.run, basis, steps }, failures: a.failures + (e.result.ok ? 0 : 1) };
    }
    case 'retry':
      if (a.run.phase !== 'landed') return a;
      return withSteps(a, a.run.steps.map((s) => (s.status.at === 'failed' || s.status.at === 'waiting' ? { ...s, status: { at: 'sending' } } : s)));
    case 'superseded':
      if (a.run.phase !== 'landed') return a;
      return withSteps(a, a.run.steps.filter((s) => !e.keys.some((k) => k.toLowerCase() === stepKey(s.step))));
    case 'left':
      if (a.run.phase === 'refused') return { ...a, run: { phase: 'idle' } };
      if (a.run.phase !== 'landed') return a;
      return withSteps(a, a.run.steps.filter((s) => s.status.at !== 'done'));
  }
}

/** A publish is running, or its hand-off is still being sent: another publish waits. */
export function blocksPublish(a: Aftermath): boolean {
  return a.run.phase === 'publishing' || (a.run.phase === 'landed' && a.run.steps.some((s) => s.status.at === 'sending'));
}

/** The steps that failed. */
export function failedSteps(a: Aftermath): HandOffStep[] {
  return a.run.phase === 'landed' ? a.run.steps.filter((s) => s.status.at === 'failed').map((s) => s.step) : [];
}

/** The steps Retry sends again: those that failed, and those held behind a failed review request. */
export function retrySteps(a: Aftermath): HandOffStep[] {
  return a.run.phase === 'landed' ? a.run.steps.filter((s) => s.status.at === 'failed' || s.status.at === 'waiting').map((s) => s.step) : [];
}
